import React, { useRef, useState } from 'react';
import { Upload, X, File as FileIcon, Loader2 } from 'lucide-react';
import { readFileAsDataUrl } from '../../../utils/attachments';

const INPUT_BASE_CLASS =
  'w-full rounded-lg px-2 py-1.5 text-xs text-slate-700 border border-gray-300 bg-surface-muted shadow-sm focus:border-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-dark/40 transition disabled:cursor-not-allowed disabled:opacity-70 disabled:bg-gray-100';

/**
 * Resolves display text from value. Shows file name or file object name.
 * @param {Object|File|string} value - File object, { file, displayText, ... } or string
 * @returns {string}
 */
function getDisplayValue(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (value instanceof File) return value.name;
  if (typeof value === 'object' && value.file) {
    return value.file.name ?? value.displayText ?? value.originalName ?? '';
  }
  if (typeof value === 'object' && value.displayText) return value.displayText;
  if (typeof value === 'object' && value.originalName) return value.originalName;
  if (typeof value === 'object' && value.name) return value.name;
  return '';
}

/**
 * File upload component: label, read-only field showing selected file, and Upload button.
 * Clicking Upload opens file picker. Supports single file selection.
 *
 * @param {Object} props
 * @param {string} props.id - Input id
 * @param {string} [props.label] - Label
 * @param {boolean} [props.required]
 * @param {string} [props.error]
 * @param {File|Object|string} props.value - Current file: File object, { file, displayText, ... } or string
 * @param {function(File|Object|null): void} props.onChange - Called with File object or { file, displayText, data, ... } or null
 * @param {string} [props.placeholder] - Placeholder when no file selected
 * @param {number} [props.labelWidthRem]
 * @param {'top'|'left'} [props.labelPosition]
 * @param {boolean} [props.disabled]
 * @param {string} [props.accept] - File types to accept (e.g., "image/*", ".pdf,.doc")
 * @param {boolean} [props.multiple] - Allow multiple file selection
 * @param {number} [props.maxSize] - Maximum file size in bytes
 * @param {boolean} [props.prepareAsDataUrl] - If true, prepare file as data URL in onChange
 */
export function FileUpload({
  id,
  label,
  required = false,
  error,
  value = null,
  onChange,
  placeholder = 'No file selected',
  labelWidthRem = 8,
  labelPosition = 'top',
  disabled = false,
  accept,
  multiple = false,
  maxSize,
  prepareAsDataUrl = false,
}) {
  const fileInputRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const errorId = id ? `${id}-error` : undefined;
  const isTop = labelPosition === 'top';

  const displayValue = getDisplayValue(value);
  const hasFile = value != null && displayValue !== '';

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) {
      onChange?.(null);
      return;
    }

    // Validate file size if maxSize is provided
    if (maxSize) {
      const oversizedFiles = files.filter((file) => file.size > maxSize);
      if (oversizedFiles.length > 0) {
        const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
        alert(`File size exceeds maximum allowed size of ${maxSizeMB} MB`);
        event.target.value = '';
        return;
      }
    }

    setIsProcessing(true);
    try {
      if (multiple) {
        // Multiple files: return array
        if (prepareAsDataUrl) {
          const preparedFiles = await Promise.all(
            files.map(async (file) => {
              const data = await readFileAsDataUrl(file);
              return {
                file,
                originalName: file.name,
                mimeType: file.type,
                size: file.size,
                data,
                displayText: file.name,
              };
            })
          );
          onChange?.(preparedFiles);
        } else {
          onChange?.(files);
        }
      } else {
        // Single file: return single file or object
        const file = files[0];
        if (prepareAsDataUrl) {
          const data = await readFileAsDataUrl(file);
          onChange?.({
            file,
            originalName: file.name,
            mimeType: file.type,
            size: file.size,
            data,
            displayText: file.name,
          });
        } else {
          onChange?.(file);
        }
      }
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Error processing file. Please try again.');
    } finally {
      setIsProcessing(false);
      // Reset input so same file can be selected again if needed
      event.target.value = '';
    }
  };

  const handleRemove = () => {
    onChange?.(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const labelEl = label ? (
    <label htmlFor={id} className="text-sm font-medium text-primary-dark">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
  ) : null;

  const fieldContent = (
    <div className="min-w-0">
      <div className="flex gap-0 rounded border border-gray-300 bg-white overflow-hidden">
        <div className="flex-1 min-w-0 flex items-center px-2 py-1.5">
          {hasFile ? (
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <FileIcon className="w-4 h-4 text-slate-500 shrink-0" />
              <span className="text-xs text-slate-700 truncate">{displayValue}</span>
            </div>
          ) : (
            <span className="text-xs text-slate-400">{placeholder}</span>
          )}
        </div>
        {hasFile && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={disabled || isProcessing}
            className="px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 border-l border-gray-300 focus:outline-none focus:ring-1 focus:ring-red-500/40 disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="Remove file"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => !disabled && !isProcessing && fileInputRef.current?.click()}
            disabled={disabled || isProcessing}
            className="h-full px-3 py-1.5 text-xs font-medium text-slate-700 bg-gray-100 border-l border-gray-300 hover:bg-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-dark/40 focus:ring-inset disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
            aria-haspopup="false"
            aria-label="Upload file"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <Upload className="w-3.5 h-3.5" />
                <span>Upload</span>
              </>
            )}
          </button>
        </div>
      </div>
      <input
        ref={fileInputRef}
        id={id}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled || isProcessing}
        onChange={handleFileSelect}
        className="hidden"
        aria-invalid={error ? true : undefined}
        aria-describedby={errorId}
      />
      {error && (
        <p id={errorId} className="mt-1 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );

  return (
    <>
      {isTop ? (
        <div className="space-y-1">
          {labelEl}
          {fieldContent}
        </div>
      ) : (
        <div
          className="grid items-center gap-y-1"
          style={{ gridTemplateColumns: `${labelWidthRem}rem 1fr` }}
        >
          {labelEl ?? <div />}
          {fieldContent}
        </div>
      )}
    </>
  );
}

export default FileUpload;
